import React, { useState, useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import Collapse from '@mui/material/Collapse';
import Box from '../../../Box';
import { ArrowRight, ExpandMore, ExpandLess } from '../../../Icons/Core';
import { ListItemIcon, ListItemText } from '../../../List';
import MenuItem from '../../../MenuItem';
import { NavigationSubmenu } from '../NavigationSubmenu/NavigationSubmenu';
const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
    minWidth: 0,
    padding: 0,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiSvgIcon-root': {
        fill: theme.palette.icon.static.emphasis.color,
        width: theme.palette.icon.size.small,
        height: theme.palette.icon.size.small,
        fontSize: theme.palette.icon.size.small
    }
}));
const StyledMenuItem = styled(MenuItem, {
    shouldForwardProp: prop => prop !== 'nested'
})(({ nested, theme }) => (Object.assign({ display: 'flex', justifyContent: 'space-between', fontWeight: theme.typography.fontWeightBold }, (nested && {
    paddingLeft: theme.palette.menuItem.padding.nested
}))));
const matchLocation = (basePath, pathname) => {
    if (!basePath || !pathname)
        return false;
    return pathname.startsWith(basePath);
};
const toCamelCase = (str) => str
    .trim()
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
    .replace(/^(.)/, (_, chr) => chr.toLowerCase());
export const NavigationBarRoutesMenu = ({ route, currentPath, onNavigate, onClose }) => {
    const [expandedItems, setExpandedItems] = useState(new Set());
    const [menuStack, setMenuStack] = useState([route]);
    const currentMenu = menuStack[menuStack.length - 1];
    const expandedSet = useMemo(() => {
        const expandedSet = new Set();
        const expandParentAndChildren = (routes, currentPath) => {
            routes.forEach(subroute => {
                if (subroute.expanded) {
                    expandedSet.add(subroute.name);
                }
                if (subroute.path && matchLocation(subroute.path, currentPath)) {
                    expandedSet.add(subroute.name);
                }
                if (subroute.children) {
                    expandParentAndChildren(subroute.children, currentPath);
                    if (subroute.children.some(child => expandedSet.has(child.name))) {
                        expandedSet.add(subroute.name);
                    }
                }
            });
        };
        expandParentAndChildren(route.children || [], currentPath);
        return expandedSet;
    }, [currentPath, route.children]);
    useEffect(() => {
        setExpandedItems(expandedSet);
    }, [expandedSet]);
    const handleItemClick = (event, isFinalChild, subroute, routeId, isFirstLevel) => {
        // Handle final child navigation
        if (isFinalChild && subroute.path) {
            event.preventDefault(); // Prevent default link behavior
            onNavigate(subroute.path); // Navigate to the subroute path
            return;
        }
        // Toggle expansion of first-level items with children
        if (isFirstLevel && subroute.children) {
            toggleExpansion(routeId);
            return;
        }
        // Navigate to a submenu if it has children
        if (subroute.children) {
            navigateToSubMenu(subroute);
        }
    };
    // Helper function to toggle expansion of a route
    const toggleExpansion = (routeId) => {
        setExpandedItems(prevExpanded => {
            const newExpanded = new Set(prevExpanded);
            newExpanded.has(routeId) ? newExpanded.delete(routeId) : newExpanded.add(routeId);
            return newExpanded;
        });
    };
    // Helper function to navigate to a submenu
    const navigateToSubMenu = (subroute) => {
        setMenuStack(prevStack => [...prevStack, subroute]);
    };
    const handleBack = () => {
        setMenuStack(prevStack => prevStack.slice(0, -1));
    };
    const renderMenuItem = (subroute, routeId, isFirstLevel, nested) => {
        var _a, _b;
        const isFinalChild = !((_a = subroute.children) === null || _a === void 0 ? void 0 : _a.length);
        const hasChildren = !!((_b = subroute.children) === null || _b === void 0 ? void 0 : _b.length);
        const isExpanded = expandedItems.has(routeId) || !!subroute.expanded;
        return (React.createElement(StyledMenuItem, { "data-testid": toCamelCase(subroute.name), nested: !!nested, key: subroute.name, onClick: event => handleItemClick(event, isFinalChild, subroute, routeId, isFirstLevel), disabled: subroute.disabled, selected: !!subroute.path && matchLocation(subroute.path, currentPath), tabIndex: 0 },
            subroute.icon && React.createElement(StyledListItemIcon, null, subroute.icon),
            subroute.path ? (React.createElement(Box
            /*
             ** Set tabIndex to -1 to prevent the link from being focusable
             ** focus is handled above by the MenuItem component
             */
            , { 
                /*
                 ** Set tabIndex to -1 to prevent the link from being focusable
                 ** focus is handled above by the MenuItem component
                 */
                component: "a", tabIndex: -1, href: subroute.path, style: { textDecoration: 'none', color: 'inherit', flex: 1 } },
                React.createElement(ListItemText, null, subroute.name))) : (React.createElement(ListItemText, null, subroute.name)),
            isFirstLevel && hasChildren ? (React.createElement(StyledListItemIcon, null, isExpanded ? React.createElement(ExpandLess, null) : React.createElement(ExpandMore, null))) : (hasChildren && (React.createElement(StyledListItemIcon, null,
                React.createElement(ArrowRight, null))))));
    };
    const renderSubMenu = (subroute, parentId, nestedLevel) => {
        const isFirstLevelItem = nestedLevel === 0;
        const isExpanded = expandedItems.has(parentId);
        return isFirstLevelItem && subroute.children ? (React.createElement(React.Fragment, { key: subroute.name },
            renderMenuItem(subroute, parentId, true, false),
            React.createElement(Collapse, { in: isExpanded },
                React.createElement(Box, { sx: { paddingLeft: 2 } }, subroute.children.map(subsubroute => renderSubMenu(subsubroute, subsubroute.name, nestedLevel + 1)))))) : (renderMenuItem(subroute, parentId, false));
    };
    const renderMenu = (menu) => {
        var _a;
        return (React.createElement(Box, { sx: { padding: 0, margin: 0, display: 'flex', flexDirection: 'column' } }, (_a = menu.children) === null || _a === void 0 ? void 0 : _a.map(subroute => renderSubMenu(subroute, subroute.name, 0))));
    };
    return (React.createElement(NavigationSubmenu, { handleBack: menuStack.length > 1 ? handleBack : undefined, title: currentMenu.name, onClose: onClose }, renderMenu(currentMenu)));
};
//# sourceMappingURL=NavigationBarRoutesMenu.js.map